'use client';

import { useState, useEffect, useRef, useMemo, memo } from 'react';
import { SliceZone } from '@prismicio/react';
import { homeComponents } from '@/slices/home-components';
import { PageDocumentDataSlicesSlice } from '../../../prismicio-types';

interface HomePageClientProps {
  remainingSlices: PageDocumentDataSlicesSlice[];
}

// Memoized LazySlice component to prevent unnecessary re-renders
const LazySlice = memo(
  ({
    slice,
    priority,
    observerMargin,
  }: {
    slice: PageDocumentDataSlicesSlice;
    priority: boolean;
    observerMargin: string;
  }) => {
    const [isVisible, setIsVisible] = useState(priority);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (priority || isVisible) return;

      const element = ref.current;
      if (!element) return;

      // Use requestIdleCallback for better performance
      const scheduleObserver = () => {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setIsVisible(true);
                observer.disconnect();
              }
            });
          },
          {
            rootMargin: observerMargin,
            threshold: 0.01, // Trigger early for smoother loading
          },
        );

        observer.observe(element);

        return () => observer.disconnect();
      };

      if ('requestIdleCallback' in window) {
        const idleId = window.requestIdleCallback(scheduleObserver);
        return () => window.cancelIdleCallback(idleId);
      } else {
        return scheduleObserver();
      }
    }, [priority, isVisible, observerMargin]);

    if (isVisible) {
      return (
        <SliceZone
          slices={[slice]}
          components={homeComponents}
          key={slice.id ?? slice.slice_type}
        />
      );
    }

    return <div ref={ref} style={{ minHeight: 200 }} />;
  },
);

LazySlice.displayName = 'LazySlice';

export default function HomePageClient({
  remainingSlices,
}: HomePageClientProps) {
  const IMMEDIATE_SLICE_COUNT = 2;

  // Detect mobile once and memoize
  const observerMargin = useMemo(() => {
    if (typeof window === 'undefined') return '200px';
    return window.innerWidth < 768 ? '100px' : '200px';
  }, []);

  return (
    <div>
      {remainingSlices.map((slice, index) => (
        <LazySlice
          key={slice.id ?? `${slice.slice_type}-${index}`}
          slice={slice}
          priority={index < IMMEDIATE_SLICE_COUNT}
          observerMargin={observerMargin}
        />
      ))}
    </div>
  );
}
